import type {
  NextQueriesAdapter,
  PlatformQueryRankingEntity,
  PromotionAdapter,
  QueryRankingAdapter,
  RelatedTagsAdapter,
} from '@empathyco/playboard-core-domain'
import type { EqualizeAdapter } from '../modules/equalize'
import type { AuditAdapterProvider } from './interfaces'
// @ts-strict-ignore
import { BlacklistProcessor } from '@empathybroker/tsclient-play/dist/processors/blacklist.processor'
import { BrowseConfigurationProcessor } from '@empathybroker/tsclient-play/dist/processors/browse-configuration.processor'
import { PromotionProcessor } from '@empathybroker/tsclient-play/dist/processors/promotion.processor'
import { RedirectionProcessor } from '@empathybroker/tsclient-play/dist/processors/redirection.processor'
import { SynonymProcessor } from '@empathybroker/tsclient-play/dist/processors/synonym.processor'
import { interfaces } from 'inversify'
import { TYPES } from '../config/inversify.types'
import { AuditTypeEnum } from '../enums'
import Context = interfaces.Context

export function getPlatformAuditAdapterProvider({ container }: Context): AuditAdapterProvider {
  return (type: string) => {
    switch (type) {
      case AuditTypeEnum.BLACKLIST:
        return { decode: payload => new BlacklistProcessor(false).decodeResponseDataItem(payload) }
      case AuditTypeEnum.BROWSE_CONFIGURATION:
        return {
          decode: payload => new BrowseConfigurationProcessor().decodeResponseDataItem(payload),
        }
      case AuditTypeEnum.EQUALIZE:
        return container.get<EqualizeAdapter>(TYPES.EqualizeAdapter)
      case AuditTypeEnum.NEXT_QUERIES:
        return {
          decode: payload =>
            container.get<NextQueriesAdapter>(TYPES.NextQueriesAdapter).decodeAudit(payload),
        }
      case AuditTypeEnum.PROMOTION:
        return {
          decode: payload =>
            container
              .get<PromotionAdapter>(TYPES.PromotionAdapter)
              .decode(new PromotionProcessor(false).decodeResponseDataItem(payload)),
        }
      case AuditTypeEnum.REDIRECTION:
        return {
          decode: payload => new RedirectionProcessor(false).decodeResponseDataItem(payload),
        }
      case AuditTypeEnum.RELATED_TAGS:
        return {
          decode: payload =>
            container.get<RelatedTagsAdapter>(TYPES.RelatedTagsAdapter).decodeAudit(payload),
        }
      case AuditTypeEnum.SEARCH_CONFIGURATION:
        return {
          decode: payload => queryRankingAuditDecode(container, payload),
        }
      case AuditTypeEnum.SYNONYM:
        return { decode: payload => new SynonymProcessor(false).decodeResponseDataItem(payload) }
      default:
        return undefined
    }
  }
}

function mapQueryRankingHiddenElements(
  elements: Array<
    Omit<
      PlatformQueryRankingEntity['features']['queryRanking']['config']['hiddenElements'][number],
      'type'
    > & {
      type: 'ATTRIBUTES' | 'PRODUCTS' | 'ATTRIBUTE' | 'PRODUCT'
    }
  >,
): PlatformQueryRankingEntity['features']['queryRanking']['config']['hiddenElements'] {
  return elements.map(element => {
    if (element.type === 'ATTRIBUTE') {
      element.type = 'ATTRIBUTES'
    }

    if (element.type === 'PRODUCT') {
      element.type = 'PRODUCTS'
    }

    return element as PlatformQueryRankingEntity['features']['queryRanking']['config']['hiddenElements'][number]
  })
}

export function queryRankingAuditDecode(
  container: interfaces.Context['container'],
  payload: PlatformQueryRankingEntity,
): ReturnType<QueryRankingAdapter['decode']> {
  if (payload.features?.queryRanking?.config?.hiddenElements) {
    payload.features.queryRanking.config.hiddenElements = mapQueryRankingHiddenElements(
      payload.features.queryRanking.config.hiddenElements,
    )
  }

  return container.get<QueryRankingAdapter>(TYPES.QueryRankingAdapter).decode(payload)
}
